import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { api, fetchBranding } from "@/lib/api";
import PlaceholderLogo from "./PlaceholderLogo";

/**
 * Marketplace-style sticky header. Used across public pages (home, lander,
 * about, checkout). Dark-navy brand mark on the left, main nav in the
 * middle, primary CTAs on the right — and on <1024px viewports a
 * hamburger button opens a full-width dropdown with the same links.
 */
export default function SiteHeader({ variant = "default", brandName }) {
  const loc = useLocation();
  const [brand, setBrand] = useState({ brand_name: brandName || "CasperDomains" });
  const [menuOpen, setMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catOpen, setCatOpen] = useState(false);

  useEffect(() => {
    if (!brandName) fetchBranding().then(setBrand).catch(() => {});
  }, [brandName]);

  useEffect(() => {
    api.get("/listings/categories")
      .then((r) => setCategories(r.data?.categories || []))
      .catch(() => setCategories([]));
  }, []);

  // Close the mobile menu on any route change so a nav click doesn't
  // leave the drawer stuck open behind the destination page.
  useEffect(() => { setMenuOpen(false); setCatOpen(false); }, [loc.pathname, loc.hash]);

  // Lock body scroll while the mobile drawer is open so background
  // content doesn't scroll under the overlay.
  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [menuOpen]);

  return (
    <>
      <header className={`cd-header ${variant === "solid" ? "cd-header--solid" : ""}`} data-testid="site-header">
      <div className="cd-container cd-header__inner">
        <Link to="/" className="cd-brand" aria-label={`${brand.brand_name} home`} data-testid="site-header-brand">
          {brand.logo_url ? (
            <img
              src={brand.logo_url}
              alt={brand.brand_name}
              data-testid="site-header-logo"
              className="cd-header__logo"
            />
          ) : (
            <PlaceholderLogo brandName={brand.brand_name} />
          )}
        </Link>

        <nav className="cd-nav" aria-label="Primary">
          <div
            style={{ position: "relative" }}
            onMouseEnter={() => setCatOpen(true)}
            onMouseLeave={() => setCatOpen(false)}
          >
            <button
              type="button"
              data-testid="nav-categories"
              onClick={() => setCatOpen((v) => !v)}
              style={{
                background: "transparent", border: "none", padding: 0, cursor: "pointer",
                font: "inherit", color: "inherit",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}
              aria-expanded={catOpen}
            >
              Categories <ChevronDown size={12} strokeWidth={2} />
            </button>
            {catOpen && categories.length > 0 && (
              <div
                data-testid="categories-mega-menu"
                style={{
                  position: "absolute", top: "100%", left: -12,
                  marginTop: 4, minWidth: 260,
                  background: "white",
                  border: "1px solid var(--cd-gray-200)",
                  borderRadius: 10,
                  boxShadow: "0 12px 32px rgba(3,20,47,.12)",
                  padding: 8, zIndex: 30,
                }}
              >
                {categories.map((c) => (
                  <Link
                    key={c.key}
                    to={c.count > 0 ? `/category/${c.key.toLowerCase().replace(/\s+/g, "-")}` : "#"}
                    data-testid={`mega-menu-${c.key}`}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "8px 12px", borderRadius: 6,
                      fontSize: 13, fontWeight: 500,
                      color: c.count > 0 ? "var(--cd-navy-950)" : "var(--cd-gray-400)",
                      textDecoration: "none",
                      pointerEvents: c.count > 0 ? "auto" : "none",
                    }}
                    onMouseEnter={(e) => c.count > 0 && (e.currentTarget.style.background = "var(--cd-gray-50)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <span>{c.key}</span>
                    <span style={{ fontSize: 11, color: "var(--cd-gray-500)", fontVariantNumeric: "tabular-nums" }}>
                      {c.count}
                    </span>
                  </Link>
                ))}
                <div style={{ borderTop: "1px solid var(--cd-gray-100)", marginTop: 6, paddingTop: 6 }}>
                  <Link
                    to="/premium"
                    style={{
                      display: "flex", justifyContent: "space-between",
                      padding: "8px 12px", borderRadius: 6,
                      fontSize: 12, fontWeight: 700, letterSpacing: ".02em",
                      color: "#92400e", textDecoration: "none",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#fffbeb")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    data-testid="mega-menu-premium"
                  >
                    <span>★ Premium picks</span>
                    <span>→</span>
                  </Link>
                  <Link
                    to="/brands"
                    style={{
                      display: "flex", justifyContent: "space-between",
                      padding: "8px 12px", borderRadius: 6,
                      fontSize: 12, fontWeight: 700, letterSpacing: ".02em",
                      color: "var(--cd-blue-500, #2563eb)", textDecoration: "none",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#eff6ff")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    data-testid="mega-menu-brands"
                  >
                    <span>◆ Brands ready-to-go</span>
                    <span>→</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
          <Link to="/projects" data-testid="nav-projects">Projects</Link>
          <Link to="/brands" data-testid="nav-brands">Brands</Link>
          <Link to="/about" data-testid="nav-about">About</Link>
        </nav>

        <div className="cd-header__actions">
          <Link to="/login" className="cd-btn cd-btn--outline cd-btn--sm" data-testid="nav-signin">Sign in</Link>
          <Link to="/#featured" className="cd-btn cd-btn--primary cd-btn--sm" data-testid="nav-browse-domains">Browse domains</Link>

          {/* Hamburger — only rendered on <1024px via CSS. Placed here so
              the flex ordering keeps it on the far right. */}
          <button
            type="button"
            className="cd-header__hamburger"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="site-header-hamburger"
          >
            {menuOpen ? <X size={22} strokeWidth={1.75} /> : <Menu size={22} strokeWidth={1.75} />}
          </button>
        </div>
      </div>
      </header>

      {/* Scrim behind the mobile drawer — click to close. Rendered
          unconditionally so its opacity can transition; pointer events
          only fire when open (see CSS). Rendered outside <header> so
          the header's backdrop-filter doesn't become a containing block
          for these fixed-positioned elements. */}
      <div
        className={`cd-header__scrim ${menuOpen ? "is-open" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile drawer — slides in from the right, full viewport height. */}
      <div
        className={`cd-header__drawer ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
        data-testid="site-header-drawer"
      >
        <div className="cd-container">
          <nav className="cd-header__drawer-nav" aria-label="Mobile primary">
            <Link to="/#categories" data-testid="drawer-categories">Categories</Link>
            <Link to="/projects" data-testid="drawer-projects">Projects</Link>
            <Link to="/brands" data-testid="drawer-brands">Brands</Link>
            <Link to="/about" data-testid="drawer-about">About</Link>
          </nav>
          <div className="cd-header__drawer-cta">
            <Link to="/login" className="cd-btn cd-btn--outline cd-btn--full" data-testid="drawer-signin">Sign in</Link>
            <Link to="/#featured" className="cd-btn cd-btn--primary cd-btn--full" data-testid="drawer-browse">Browse domains</Link>
          </div>
        </div>
      </div>
    </>
  );
}
